import type { ChartConfig, ChartMode } from '../type/dynamicChart';
import { validateChartConfig } from './chartDefaults';
import { migrateChartFilters } from './filterUtils';
import { StorageService } from '@/lib/StorageService';

const storageService = new StorageService();

export interface ChartConfigCollection {
  version: string;
  mode: ChartMode;
  createdAt: string;
  configs: ChartConfig[];
}

const STORAGE_KEY_PREFIX = 'toollife_chart_config';
const CURRENT_VERSION = '2.0';

function getStorageKey(mode: ChartMode): string {
  return `${STORAGE_KEY_PREFIX}_${mode}`;
}

/**
 * Migrate stored data from any supported version to the current format.
 * Returns an array of ChartConfig on success, or null on failure.
 *
 * v1.0 → v2.0: converts old ChartFilters object to FilterRule[]; validates aggregation.
 */
export function migrate(stored: unknown): ChartConfig[] | null {
  if (!stored || typeof stored !== 'object') return null;

  const obj = stored as Record<string, unknown>;

  if (!obj.version || typeof obj.version !== 'string') return null;
  if (!Array.isArray(obj.configs)) return null;

  if (obj.version === '1.0') {
    return (obj.configs as unknown[]).map((raw) => {
      const config = raw as ChartConfig & { filters: unknown };
      return validateChartConfig({ ...config, filters: migrateChartFilters(config.filters) });
    });
  }

  if (obj.version === '2.0') {
    return (obj.configs as unknown[]).map((raw) => validateChartConfig(raw as ChartConfig));
  }

  return null;
}

export function exportChartConfig(configs: ChartConfig[], mode: ChartMode): void {
  const collection: ChartConfigCollection = {
    version: CURRENT_VERSION,
    mode,
    createdAt: new Date().toISOString(),
    configs,
  };

  const blob = new Blob([JSON.stringify(collection, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `toollife-charts-${mode}-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export async function importChartConfig(
  file: File,
  currentMode: ChartMode
): Promise<ChartConfigCollection | null> {
  try {
    const text = await file.text();
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      console.warn('Corrupt JSON in imported chart config');
      return null;
    }

    const migrated = migrate(parsed);
    if (!migrated) {
      const version = (parsed as Record<string, unknown>)?.version;
      console.warn(`Configuration version not supported: ${String(version)}`);
      return null;
    }

    const obj = parsed as Record<string, unknown>;
    const importedMode = obj.mode as ChartMode;

    if (importedMode !== currentMode) {
      console.warn(`Mode mismatch: imported ${importedMode}, current ${currentMode}`);
      return {
        version: CURRENT_VERSION,
        mode: currentMode,
        createdAt: String(obj.createdAt ?? new Date().toISOString()),
        configs: migrated.map((config) => validateChartConfig(config, currentMode)),
      };
    }

    return {
      version: CURRENT_VERSION,
      mode: importedMode,
      createdAt: String(obj.createdAt ?? new Date().toISOString()),
      configs: migrated,
    };
  } catch (error) {
    console.error('Failed to import chart configuration:', error);
    return null;
  }
}

export function saveDefaultConfig(configs: ChartConfig[], mode: ChartMode): void {
  const collection: ChartConfigCollection = {
    version: CURRENT_VERSION,
    mode,
    createdAt: new Date().toISOString(),
    configs,
  };

  try {
    storageService.setChartConfig(getStorageKey(mode), JSON.stringify(collection));
  } catch (error) {
    console.error('Failed to save default configuration:', error);
    throw new Error('Failed to save configuration to local storage', { cause: error });
  }
}

/**
 * Load default chart configurations from localStorage.
 * Returns null if no default exists, if JSON is corrupt, or if migration fails.
 */
export function loadDefaultConfig(mode: ChartMode): ChartConfig[] | null {
  try {
    const stored = storageService.getChartConfig(getStorageKey(mode));
    if (!stored) return null;

    let parsed: unknown;
    try {
      parsed = JSON.parse(stored);
    } catch {
      console.warn('Corrupt JSON in stored chart config — falling back to defaults');
      return null;
    }

    const obj = parsed as Record<string, unknown>;

    if (obj.mode !== mode) {
      console.warn(`Mode mismatch in stored config: ${String(obj.mode)} vs ${mode}`);
      return null;
    }

    const migrated = migrate(parsed);
    if (!migrated) {
      console.warn(`Could not migrate stored config version: ${String(obj.version)}`);
      return null;
    }

    return migrated;
  } catch (error) {
    console.error('Failed to load default configuration:', error);
    return null;
  }
}

export function clearDefaultConfig(mode: ChartMode): void {
  try {
    storageService.removeChartConfig(getStorageKey(mode));
  } catch (error) {
    console.error('Failed to clear default configuration:', error);
    throw new Error('Failed to clear configuration from local storage', { cause: error });
  }
}

export function hasDefaultConfig(mode: ChartMode): boolean {
  return storageService.getChartConfig(getStorageKey(mode)) !== null;
}
